// ============================================================
// api/tenants.js
// Endpoint bindings for the mgmt/admin/tenants endpoints
// (Workstream 1, shipped May 2026).
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-tenants-list.js
//   backend/func-carehub-api/src/functions/admin-tenants-create.js
//   APIM operations: mgmt-admin-tenants-list, mgmt-admin-tenants-create
// ============================================================

import { apiGet, apiPost, apiPatch } from './client';

/**
 * Fetch a single Tenant by ID, including computed aggregates
 * (clientCount, activeUserCount, invitationsPendingCount).
 *
 * Soft-deleted Tenants (isActive=false) DO return — the admin
 * portal needs to see them for audit purposes. 404 only fires
 * when the row genuinely doesn't exist.
 *
 * @param {number} tenantId
 *
 * @returns {Promise<{
 *   tenantId: number,
 *   tenantName: string,
 *   identityProvider: 'm365'|'google_workspace',
 *   externalTenantId: string|null,
 *   notes: string|null,
 *   config: string|null,
 *   isActive: boolean,
 *   deactivatedAt: string|null,
 *   deactivatedBy: string|null,
 *   createdAt: string|null,
 *   createdBy: string,
 *   updatedAt: string|null,
 *   updatedBy: string|null,
 *   clientCount: number,
 *   activeUserCount: number,
 *   invitationsPendingCount: number,
 * }>}
 */
export async function getAdminTenantDetail(tenantId) {
  return apiGet(`/mgmt/admin/tenants/${tenantId}`);
}

/**
 * List Tenants with pagination + optional search filter.
 *
 * @param {Object}  [params={}]
 * @param {number}  [params.page=1]      1-indexed
 * @param {number}  [params.pageSize=50] 1..200
 * @param {string}  [params.search]      Substring match against TenantName
 *
 * @returns {Promise<{
 *   items: Array<{
 *     tenantId: number,
 *     tenantName: string,
 *     identityProvider: 'm365'|'google_workspace',
 *     externalTenantId: string|null,
 *     isActive: boolean,
 *     createdAt: string|null,
 *     clientCount: number,
 *   }>,
 *   totalCount: number,
 *   page: number,
 *   pageSize: number,
 * }>}
 */
// params: page, pageSize, search, identityProvider (exact match on the
// shared vocabulary — the Tenants list filter, 2026-09-04).
export async function listTenants(params = {}) {
  return apiGet('/mgmt/admin/tenants', params);
}

/**
 * Create a Tenant.
 *
 * @param {Object} body
 * @param {string} body.tenantName
 * @param {('m365'|'google_workspace')} body.identityProvider
 * @param {string} [body.externalTenantId]
 *
 * @returns {Promise<{
 *   tenantId: number, tenantName: string,
 *   identityProvider: 'm365'|'google_workspace',
 *   externalTenantId: string|null, isActive: boolean,
 *   createdAt: string|null,
 * }>}
 */
export async function createTenant(body) {
  return apiPost('/mgmt/admin/tenants', body);
}

/**
 * Edit a Tenant's descriptive identity facts (2026-09-03). Any subset
 * of the three fields; `externalTenantId: null` clears it.
 *
 * Backend: admin-tenants-update.js. 409 `federation_active` when the
 * provider would move away from m365 while federation is active.
 *
 * @param {number} tenantId
 * @param {{ tenantName?: string, identityProvider?: ('m365'|'google_workspace'|'other'), externalTenantId?: string|null }} body
 * @returns {Promise<{ tenantId:number, tenantName:string, identityProvider:string, externalTenantId:string|null, wasIdempotent:boolean }>}
 */
export async function updateTenant(tenantId, body) {
  if (!Number.isInteger(tenantId) || tenantId <= 0) {
    throw new TypeError(`updateTenant: tenantId must be a positive integer, got: ${JSON.stringify(tenantId)}`);
  }
  return apiPatch(`/mgmt/admin/tenants/${tenantId}`, body);
}
